import type { CreateAttachmentsFunc } from '../types/CreateAttachmentsFunc';
import { logUsage } from 'owa-analytics';
import { AttachmentFileType, LocalComputerFile } from 'owa-attachment-file-types';
import {
    AttachmentWellViewState,
    lazyGetAvailableLocalAttachmentSize,
    CreateAttachmentsProperties,
} from 'owa-attachment-well-data';
import {
    CreateAttachmentHandler,
    CreateAttachmentHandlerParams,
    lazyAttachmentActionsPanel,
    SelectedFilesSource,
    SizeLimitInKbToSuggestSharing,
    ComposeUniqueState,
    InsertLinkHandler,
} from 'owa-attachment-filepicker';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import { isDataProviderAvailable } from 'owa-fileprovider-store/lib/utils/isDataProviderAvailable';
import attachmentMultiMenuEntry from './attachmentMultiMenuEntry';

export default function getCreateAttachmentHandlerForLocalFile(
    viewState: AttachmentWellViewState,
    createAttachment: CreateAttachmentsFunc,
    composeUniqueState: ComposeUniqueState,
    targetWindow: Window,
    insertLinkHandler: InsertLinkHandler | null,
    treatShareAsInsertLink?: boolean
): CreateAttachmentHandler {
    return async createAttachmentHandlerParams => {
        const fileObjects: File[] = [];
        for (let i = 0; i < createAttachmentHandlerParams.files.length; i++) {
            if (createAttachmentHandlerParams.files[i].fileType === AttachmentFileType.Local) {
                fileObjects.push(
                    (<LocalComputerFile>createAttachmentHandlerParams.files[i]).fileObject
                );
            } else {
                throw new Error(
                    `Unexpected file type ${createAttachmentHandlerParams.files[i].fileType}`
                );
            }
        }
        const totalSizeInBytes = fileObjects.reduce((sum, file) => sum + file.size, 0);
        const availableClassicAttachmentSizeInBytes = (
            await lazyGetAvailableLocalAttachmentSize.import()
        )(viewState);
        // We only show the dialog if
        // 1- The file sizes exceeds the size that can be attached OR
        // 2- The total size exceed the limit for suggesting to share
        const doesFilesSizeExceedsAvailableSize =
            totalSizeInBytes > availableClassicAttachmentSizeInBytes;
        const shouldSuggestSharing = totalSizeInBytes > SizeLimitInKbToSuggestSharing * 1024;
        const shouldShowActionPanel =
            (doesFilesSizeExceedsAvailableSize || shouldSuggestSharing) &&
            isDataProviderAvailable(!!viewState.groupId) &&
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (60,13): Object is possibly 'undefined'.
            // @ts-expect-error
            getAttachmentPolicy().ReferenceAttachmentsEnabled &&
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2532 (64,14): Object is possibly 'undefined'.
            // @ts-expect-error
            !getUserConfiguration().SessionSettings.IsShadowMailbox;

        const createAttachmentHandler: CreateAttachmentHandler = params => {
            const properties: CreateAttachmentsProperties = {
                isInline: params.isInline,
                isHiddenInline: false,
                shouldShare: !!params.shouldShare,
            };

            createAttachment(params.files, properties, params.dataProviderType);
        };
        const isGroupsScenario = !!viewState.groupId;

        if (shouldShowActionPanel) {
            logUsage('AFPSecondPanelShownForLargeFiles', {
                suggestSharing: shouldSuggestSharing,
                exceedsAvailbleSize: doesFilesSizeExceedsAvailableSize,
                fileCount: Math.min(fileObjects.length, 23),
            });
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2769 (86,13): No overload matches this call.
            // @ts-expect-error
            (await lazyAttachmentActionsPanel.import())(
                createAttachmentHandler,
                insertLinkHandler,
                fileObjects,
                null,
                SelectedFilesSource.LocalComputerFiles,
                availableClassicAttachmentSizeInBytes,
                composeUniqueState,
                targetWindow,
                treatShareAsInsertLink,
                isGroupsScenario,
                upsellHandler(
                    createAttachmentHandlerParams,
                    viewState,
                    insertLinkHandler,
                    createAttachment,
                    composeUniqueState,
                    targetWindow,
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2345 (107,21): Argument of type 'boolean | undefined' is not assignable to parameter of type 'boolean'.
                    // @ts-expect-error
                    treatShareAsInsertLink
                )
            );
        } else {
            createAttachmentHandler(createAttachmentHandlerParams);
        }
    };
}

function upsellHandler(
    createAttachmentHandlerParams: CreateAttachmentHandlerParams,
    viewState: AttachmentWellViewState,
    insertLinkHandler: InsertLinkHandler | null,
    createAttachment: CreateAttachmentsFunc,
    composeUniqueState: ComposeUniqueState,
    targetWindow: Window,
    treatShareAsInsertLink: boolean
) {
    return () => {
        const createAttachmentHandlerForUpsell = attachmentMultiMenuEntry.uploadAndShareFromCloud.getCreateAttachmentHandler(
            viewState,
            treatShareAsInsertLink
                ? (files, properties, _dataProvider) =>
                      // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                      // -> Error TS2721 (133,23): Cannot invoke an object which is possibly 'null'.
                      // @ts-expect-error
                      insertLinkHandler({ files, dataProviderType: null, targetWindow, properties })
                : createAttachment,
            composeUniqueState,
            targetWindow
        );
        createAttachmentHandlerForUpsell(createAttachmentHandlerParams);
    };
}
